'use client';

import styled from 'styled-components';
import { isTripAdvisorSource, isTrustYouSource } from '@/app/common/reviews/reviewSource.ts';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider.tsx';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic.tsx';
import { DEBUG_VISIBILITY, useDebugVisibility } from '@/app/components/hotel-detail/hooks/useDebugVisibility.ts';

const DebugLine = styled.div`
  font-size: ${({ theme }) => theme.kallisto.font.size['150']};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight['200']};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  word-break: break-word;
`;

const NO_ID = '—';
/** Settled, yet still on the search placeholder: travel-api static never reached this page. */
const NOT_COMMITTED = '— (search placeholder, static not committed)';

/** Only a TRIPADVISOR source makes the opinion id a TA location id — label it for what it is. */
const getOpinionIdLabel = (reviewSource?: string) => {
  if (isTripAdvisorSource(reviewSource)) return 'TA id';
  if (isTrustYouSource(reviewSource)) return 'TY id';
  return 'opinion id';
};

/**
 * `?debug=true` line with the hotel's opinion id (MPH-940).
 *
 * `hotelOpinion.opinionId` carries the TripAdvisor location id for a TRIPADVISOR hotel
 * (travel-api `opinion.source.id`) — the very id `useFetchTripAdvisorReviews` sends to
 * hotel-reviews — and the legacy TrustYou uuid for a TRUSTYOU one. It is the only part of the
 * review payload the page never shows, so a hotel rendering no score cannot be told apart from
 * one TripAdvisor has no data for. Score, review count and provider are on screen already; the
 * label carries the provider, so nothing else needs repeating here.
 *
 * Three states have to stay distinguishable, because a page can sit on the memsearch placeholder
 * (no `reviewSource`, `opinionId: ''`) and that is indistinguishable, by content, from a hotel
 * travel-api genuinely has no opinion for:
 *   - `isValidating` — the mount fetch is in flight;
 *   - settled but still the fallback by reference — static never committed. Reachable when the SWR
 *     key is null (`isValidHotelId` false), where no revalidation is ever started;
 *   - anything else — real travel-api data, so an empty id means an unmapped hotel.
 *
 * History: until MPH-964 a page carrying search context that mounted in a document not being
 * rendered (a background tab, an off-screen webview) never issued the static request at all — SWR
 * deferred its only mount revalidation through `requestAnimationFrame` because the placeholder was
 * passed as `fallbackData` — while `isValidating` was optimistically true, so this line read
 * `resolving…` with no request in flight, indefinitely. `useFetchHotelStatic` no longer seeds SWR
 * with the placeholder (see `withPlaceholderData`), so the mount fetch starts in the layout effect
 * of the first render. `isValidating` is still optimistically true for that first render, but React
 * runs layout effects in the same synchronous block as the commit, so nothing — no paint, no
 * screenshot, no DOM read — can observe that window: for any reader, `resolving…` now means a
 * request really is in flight.
 *
 * Mounted OUTSIDE the reviews gate (`hasKnownReviewSource` / `reviewsDisabled`) on purpose: the
 * case worth debugging is the one where the reviews UI renders nothing. It is still inside the
 * hero's own `staticData` gate — `EvoHeroInfoPanel` returns null and `EvoHero` shows a skeleton
 * while `staticData` is undefined — so on Evo the unresolved states only paint once a placeholder
 * exists to render around.
 */
export const DebugReviewsInfo = () => {
  const { staticHotelFallbackData } = useInitialParams();
  const { data: staticData, error, isValidating } = useFetchHotelStatic();
  const visibility = useDebugVisibility();

  // A login prompt does not belong next to the hotel title: an unauthorised viewer gets nothing.
  if (visibility !== DEBUG_VISIBILITY.VISIBLE) return null;

  const label = getOpinionIdLabel(staticData?.hotelOpinion?.reviewSource);

  if (error) return <DebugLine data-testid="debug-reviews-info">{`${label}: static fetch failed`}</DebugLine>;
  if (isValidating) return <DebugLine data-testid="debug-reviews-info">{`${label}: resolving…`}</DebugLine>;

  // Compared by reference: `useFetchHotelStatic` hands back the very `staticHotelFallbackData`
  // object while SWR has no data, and the placeholder's content cannot be told from a real payload
  // for an unmapped hotel.
  const isPlaceholder = !staticData || staticData === staticHotelFallbackData;

  return (
    <DebugLine data-testid="debug-reviews-info">
      {`${label}: ${isPlaceholder ? NOT_COMMITTED : staticData.hotelOpinion.opinionId || NO_ID}`}
    </DebugLine>
  );
};
